// src/features/admin-templates/components/EquipmentTemplatesPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useEquipmentTemplates } from '../../equipment/hooks/useEquipmentTemplates';
import { useTaskTemplates } from '../../tasks/hooks/useTaskTemplates';
import { useTemplateAdminPermissions } from '../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';

const EMPTY_FORM = {
  name: '',
  equipment_type: '',
  manufacturer: '',
  model: '',
  notes: '',
  max_voltage_kv: '',
};

function EquipmentTemplateFormFields({ f, onChange, errors, t }) {
  return (
    <Box display="flex" flexDirection="column" gap={2} pt={1}>
      <Box display="flex" gap={2}>
        <TextField
          label={t('equipment:labels.name')}
          value={f.name}
          onChange={e => onChange('name', e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          size="small"
          sx={{ flex: 2 }}
        />
        <TextField
          label={t('equipment:labels.equipment_type')}
          value={f.equipment_type}
          onChange={e => onChange('equipment_type', e.target.value)}
          error={!!errors.equipment_type}
          helperText={errors.equipment_type}
          size="small"
          sx={{ flex: 1 }}
        />
      </Box>

      <Box display="flex" gap={2}>
        <TextField
          label={t('equipment:labels.manufacturer')}
          value={f.manufacturer}
          onChange={e => onChange('manufacturer', e.target.value)}
          size="small"
          sx={{ flex: 1 }}
        />
        <TextField
          label={t('equipment:labels.model')}
          value={f.model}
          onChange={e => onChange('model', e.target.value)}
          size="small"
          sx={{ flex: 1 }}
        />
      </Box>

      <TextField
        fullWidth
        multiline
        rows={6}
        label={t('equipment:labels.notes')}
        value={f.notes}
        onChange={e => onChange('notes', e.target.value)}
      />

      <TextField
        type="number"
        label={t('equipment:labels.max_voltage_kv')}
        value={f.max_voltage_kv}
        onChange={e => onChange('max_voltage_kv', e.target.value)}
        size="small"
        sx={{ width: 180 }}
      />
    </Box>
  );
}

export default function EquipmentTemplatesPanel() {
  const { t } = useTranslation(['equipment', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canCreate, canUpdate, canDelete } = useTemplateAdminPermissions();

  const {
    templates,
    loading,
    error,
    clearError,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    linkTaskTemplate,
    unlinkTaskTemplate,
  } = useEquipmentTemplates();

  const {
    templates: taskTemplates,
    fetchAllTemplates: fetchTaskTemplates,
  } = useTaskTemplates();

  const [createOpen, setCreateOpen] = useState(false);
  const [editState, setEditState] = useState({ open: false, templateId: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [linkDialog, setLinkDialog] = useState({ open: false, template: null });
  const [taskSearch, setTaskSearch] = useState('');

  useEffect(() => {
    fetchTemplates({ include_inactive: true });
    fetchTaskTemplates();
  }, [fetchTemplates, fetchTaskTemplates]);

  const handleFormChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  const openCreate = useCallback(() => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setCreateOpen(true);
  }, []);

  const openEdit = useCallback((row) => {
    setForm({
      name: row.name ?? '',
      equipment_type: row.equipment_type ?? '',
      manufacturer: row.manufacturer ?? '',
      model: row.model ?? '',
      notes: row.notes ?? '',
      max_voltage_kv: row.max_voltage_kv ?? '',
    });
    setFormErrors({});
    setEditState({ open: true, templateId: row.equipment_template_id });
  }, []);

  const closeAll = useCallback(() => {
    setCreateOpen(false);
    setEditState({ open: false, templateId: null });
    setForm(EMPTY_FORM);
    setFormErrors({});
  }, []);

  const validate = (f) => {
    const errors = {};
    if (!f.name.trim()) errors.name = t('common:validation.required');
    if (!f.equipment_type.trim()) errors.equipment_type = t('common:validation.required');
    return errors;
  };

  const buildPayload = (f) => ({
    name: f.name.trim(),
    equipment_type: f.equipment_type.trim(),
    manufacturer: f.manufacturer.trim() || null,
    model: f.model.trim() || null,
    notes: f.notes.trim() || null,
    max_voltage_kv: f.max_voltage_kv ? parseFloat(f.max_voltage_kv) : null,
  });

  const handleCreate = async () => {
    const errors = validate(form);
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    const result = await createTemplate(buildPayload(form));
    if (result.success) {
      showSuccess(t('equipment:admin.created'));
      closeAll();
    } else {
      showError(result.error);
    }
  };

  const handleUpdate = async () => {
    const errors = validate(form);
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    const result = await updateTemplate(editState.templateId, { ...buildPayload(form), is_active: true });
    if (result.success) {
      showSuccess(t('equipment:admin.updated'));
      closeAll();
    } else {
      showError(result.error);
    }
  };

  const handleDelete = useCallback((row) => {
    showConfirmDialog({
      title: t('equipment:admin.confirm_delete_title'),
      message: t('equipment:admin.confirm_delete_message', { name: row.name }),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteTemplate(row.equipment_template_id);
        if (result.success) {
          showSuccess(t('equipment:admin.deleted'));
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, deleteTemplate, showSuccess, showError, t]);

  const openLinkDialog = useCallback((row) => {
    setLinkDialog({ open: true, template: row });
    setTaskSearch('');
  }, []);

  const handleLink = useCallback(async (taskTemplate) => {
    if (!linkDialog.template) return;
    const result = await linkTaskTemplate(linkDialog.template.equipment_template_id, taskTemplate.template_id);
    if (result.success) {
      showSuccess(t('equipment:admin.task_linked'));
      await fetchTemplates({ include_inactive: true });
      setLinkDialog(prev => ({ ...prev, template: result.data }));
    } else {
      showError(result.error);
    }
  }, [linkDialog.template, linkTaskTemplate, fetchTemplates, showSuccess, showError, t]);

  const handleUnlink = useCallback(async (taskTemplateId) => {
    if (!linkDialog.template) return;
    const result = await unlinkTaskTemplate(linkDialog.template.equipment_template_id, taskTemplateId);
    if (result.success) {
      showSuccess(t('equipment:admin.task_unlinked'));
      await fetchTemplates({ include_inactive: true });
      setLinkDialog(prev => ({ ...prev, template: result.data }));
    } else {
      showError(result.error);
    }
  }, [linkDialog.template, unlinkTaskTemplate, fetchTemplates, showSuccess, showError, t]);

  const linkedIds = linkDialog.template?.task_templates?.map(tt => tt.template_id) ?? [];
  const filteredUnlinked = taskTemplates
    .filter(tt => !linkedIds.includes(tt.template_id))
    .filter(tt => !taskSearch || tt.name.toLowerCase().includes(taskSearch.toLowerCase()) || tt.category.toLowerCase().includes(taskSearch.toLowerCase()));

  const currentDialogTemplate = linkDialog.open
    ? templates.find(tmpl => tmpl.equipment_template_id === linkDialog.template?.equipment_template_id) ?? linkDialog.template
    : null;

  const columns = [
    { field: 'name', headerName: t('equipment:labels.name'), flex: 2, minWidth: 160 },
    { field: 'equipment_type', headerName: t('equipment:labels.equipment_type'), width: 140 },
    { field: 'manufacturer', headerName: t('equipment:labels.manufacturer'), width: 140 },
    { field: 'model', headerName: t('equipment:labels.model'), width: 130 },
    {
      field: 'task_templates',
      headerName: t('equipment:labels.task_templates'),
      width: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <Chip label={row.task_templates?.length ?? 0} size="small" color={row.task_templates?.length > 0 ? 'primary' : 'default'} variant="outlined" />
      ),
    },
    {
      field: 'is_active',
      headerName: t('common:labels.active'),
      width: 80,
      renderCell: ({ row }) => (
        <Chip label={row.is_active ? t('common:labels.yes') : t('common:labels.no')} size="small" color={row.is_active ? 'success' : 'default'} variant="outlined" />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          {canUpdate && (
            <Button size="small" onClick={() => openEdit(row)} sx={{ minWidth: 0 }}>
              <EditIcon fontSize="small" />
            </Button>
          )}
          {canUpdate && (
            <Button size="small" onClick={() => openLinkDialog(row)} sx={{ minWidth: 0 }} title={t('equipment:admin.manage_task_links')}>
              <LinkIcon fontSize="small" />
            </Button>
          )}
          {canDelete && (
            <Button size="small" color="error" onClick={() => handleDelete(row)} sx={{ minWidth: 0 }}>
              <DeleteIcon fontSize="small" />
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('equipment:admin.description')}
        </Typography>

        <Box mb={2} display="flex" gap={1}>
          {canCreate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {t('equipment:admin.add_button')}
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchTemplates({ include_inactive: true })}>
            {t('common:buttons.refresh')}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>{error}</Alert>}

        <DataGrid
          rows={templates}
          columns={columns}
          getRowId={row => row.equipment_template_id}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          autoHeight
          disableRowSelectionOnClick
          sx={{ border: 0 }}
        />
      </Paper>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>{t('equipment:admin.new_template')}</DialogTitle>
        <DialogContent>
          <EquipmentTemplateFormFields f={form} onChange={handleFormChange} errors={formErrors} t={t} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>{t('common:buttons.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={loading}>{t('common:buttons.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editState.open} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>{t('equipment:admin.edit_template')}</DialogTitle>
        <DialogContent>
          <EquipmentTemplateFormFields f={form} onChange={handleFormChange} errors={formErrors} t={t} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>{t('common:buttons.cancel')}</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={loading}>{t('common:buttons.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Task template link dialog */}
      <Dialog open={linkDialog.open} onClose={() => setLinkDialog({ open: false, template: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('equipment:admin.manage_task_links_for', { name: currentDialogTemplate?.name })}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom mt={1}>{t('equipment:admin.linked_tasks')}</Typography>
          {currentDialogTemplate?.task_templates?.length === 0 ? (
            <Typography variant="body2" color="text.secondary" mb={2}>{t('equipment:admin.no_linked_tasks')}</Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
              {currentDialogTemplate?.task_templates?.map(tt => (
                <Chip
                  key={tt.template_id}
                  label={tt.name}
                  onDelete={canUpdate ? () => handleUnlink(tt.template_id) : undefined}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}

          <Typography variant="subtitle2" gutterBottom>{t('equipment:admin.add_task_link')}</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={t('common:buttons.search')}
            value={taskSearch}
            onChange={e => setTaskSearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {filteredUnlinked.length === 0 ? (
              <Typography variant="body2" color="text.secondary" p={2}>{t('equipment:admin.no_available_tasks')}</Typography>
            ) : (
              filteredUnlinked.map(tt => (
                <Box
                  key={tt.template_id}
                  sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderBottom: '1px solid', borderColor: 'divider' }}
                  onClick={() => handleLink(tt)}
                >
                  <Typography variant="body2" fontWeight="medium">{tt.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{tt.category} · {tt.frequency_interval} {tt.unit}</Typography>
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialog({ open: false, template: null })}>{t('common:buttons.close')}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog />
    </Box>
  );
}
